import halveIt from './definition.js';
import {
    getNumericKeypadHTML, getDartKeypadHTML, getBullKeypadHTML, getFortyOneKeypadHTML,
    resetKeypadEntryState,
    digitInput, digitClear, digitBackspace,
    dartTap as kpDartTap, dartHalve as kpDartHalve, dartUndo as kpDartUndo, dartClear as kpDartClear,
    fortyOneHit as kpFortyOneHit, fortyOneMiss as kpFortyOneMiss
} from '../../components/keypad.js';
import { mountDartboardForTarget, mountDartboardForBull, clearDartTaps, removeLastDartMark } from '../../components/dartboard-input.js';
import { mountRoundsGrid, updateCell, updateTotal } from '../../components/rounds-grid.js';
import { showAnimatedModal, hideAnimatedModal, isModalOpen } from '../../core/modal.js';
import { saveGameToHistory } from '../../core/db.js';
import { simulateAiChallenge } from './ai.js';
import { showCpuThrowOverlay, waitBeforeFirstDart, revealCpuDart, drawCpuDart, showAimMarker, setCpuThrowOutcome, finishCpuThrowOverlay, waitBetweenCpuTurns } from '../../components/cpu-dartboard-overlay.js';
import { wedgeColor } from '../../core/dartboard-geometry.js';

const rounds = halveIt.rounds;

let roundScores = {};
let currentPlayers = [];
let currentGameId = null;
let currentCell = null;
let onChangeCallback = null;
let dartMode = false;
let dartTarget = 0;
let dartMaxMult = 3;
const autoOpenKeypadForNextRound = false;
let observerMode = false;
// player -> throwProfile (see core/ai-difficulty.js) for whichever players
// are Computer-controlled - only ever non-empty on the device that actually
// set them up (see games/shared/x01-game-controller.js's own cpuPlayers
// comment for why). Never part of getState()/setState() - local config, not
// shared match state.
let cpuPlayers = {};
let cpuTurnInProgress = false; // re-entrancy guard - maybeRunCpuChallenge() can otherwise fire twice for the same round
// Set by stopCpuTurn() (app.js calls it when leaving the game screen) so a
// CPU's challenge in flight doesn't keep animating/committing in the
// background after the player has already navigated away.
let cpuTurnStopped = false;
let currentCpuOverlay = null;
// The live progress of a Computer challenge currently being simulated on
// this client - broadcast via getState()/setState() the same way
// x01-game-controller.js's own cpuTurnLive is, so every other connected
// client (an observer, or a human player's own device in the same session)
// opens the board the instant the challenge starts and reveals each dart the
// moment it actually lands. `roundIndex` is which round this challenge
// belongs to (needed here, unlike x01, since a challenge isn't implicitly
// "whoever's active turn it is").
let cpuTurnSeq = 0;
let cpuTurnLive = null; // { seq, player, roundIndex, plan, revealed, done } | null
// A non-driving client's own view of cpuTurnLive - see x01-game-controller.js's
// identical observerCpuSeq/observerCpuRevealed for why these are never part
// of getState()/setState() themselves.
let observerCpuSeq = 0;
let observerCpuRevealed = 0;

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

// --- ?debug=1 game simulation ---
//
// Fills every round with a plausible score instead of a flat 0-180 random
// number, so history/stats views can be exercised with data that actually
// respects each round's real scoring constraints (a "15" round can't score
// more than 9x15 - three darts, triple is the best any one of them can do).
// `missRate` (0-0.2, rolled once per simulated game) is the only way a round
// comes back 0 (and halves the running total) - every other branch always
// lands at least one dart, so it never accidentally produces a 0 on its own.

const BOARD_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// A single dart that hit the target at all is far more likely to be a
// single than a treble - this ratio is reused for every "hit the number"
// round (14-20) and doesn't need to be exact, just realistic-shaped.
function randomHitMultiplier() {
    const roll = Math.random();
    return roll < 0.6 ? 1 : roll < 0.9 ? 2 : 3;
}

// Hit-the-number rounds (14-20): up to 3 darts each at the target, each
// landing a single/double/triple of it or missing the number entirely -
// max possible is 3 darts x triple = 9x the target.
function simulateNumberRound(target, missRate) {
    if (Math.random() < missRate) return 0;
    const dartsOnTarget = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < dartsOnTarget; i++) total += randomHitMultiplier() * target;
    return total;
}

// Any double, 2 through 40 (or the bullseye, sometimes counted as one) -
// up to 3 hits, each some plausible double value.
function simulateDoublesRound(missRate) {
    if (Math.random() < missRate) return 0;
    const doubleValues = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 50];
    const hits = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < hits; i++) total += doubleValues[randomIntFromInterval(0, doubleValues.length - 1)];
    return total;
}

// Any triple, 3 through 60 - up to 3 hits.
function simulateTriplesRound(missRate) {
    if (Math.random() < missRate) return 0;
    const hits = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < hits; i++) total += randomIntFromInterval(1, 20) * 3;
    return total;
}

// 3 numerically-adjacent segments on the board (e.g. 20/1/18) - a real
// neighboring trio from the actual dartboard layout, 1-3 of them landed.
function simulateAdjacentRound(missRate) {
    if (Math.random() < missRate) return 0;
    const start = randomIntFromInterval(0, BOARD_ORDER.length - 1);
    const trio = [0, 1, 2].map(offset => BOARD_ORDER[(start + offset) % BOARD_ORDER.length]);
    const hits = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < hits; i++) total += trio[i];
    return total;
}

// 3 different-colored segments - just 1-3 plain single hits, since which
// numbers count is about the segment's color, not its value.
function simulateColorsRound(missRate) {
    if (Math.random() < missRate) return 0;
    const hits = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < hits; i++) total += randomIntFromInterval(1, 20);
    return total;
}

// Outer bull (25) or bullseye (50) - up to 3 darts, outer bull is the more
// common hit of the two.
function simulateBullsRound(missRate) {
    if (Math.random() < missRate) return 0;
    const hits = randomIntFromInterval(1, 3);
    let total = 0;
    for (let i = 0; i < hits; i++) total += Math.random() < 0.7 ? 25 : 50;
    return total;
}

// Binary hit/miss for exactly 41.
function simulateFortyOneRound(missRate) {
    return Math.random() < missRate ? 0 : 41;
}

function simulateRoundScore(round, missRate) {
    const widget = halveIt.inputWidgetFor(round);
    if (widget.type === 'dartboard-number') return simulateNumberRound(widget.target, missRate);
    if (widget.type === 'dartboard-bull') return simulateBullsRound(missRate);
    if (widget.type === 'fortyone') return simulateFortyOneRound(missRate);
    if (round.name === 'Doubles') return simulateDoublesRound(missRate);
    if (round.name === 'Triples') return simulateTriplesRound(missRate);
    if (round.name === '3 Adjacent') return simulateAdjacentRound(missRate);
    return simulateColorsRound(missRate); // '3 Colors'
}

// How a Halve It cell renders, given its stored score: exactly-0 is a
// "halved" miss (x), undefined is untouched (-), anything else is the raw
// score. Shared between the initial grid mount and keypadSubmit's updates so
// the two can never drift apart.
function halveItCellContent(player, roundIndex) {
    const score = roundScores[player][roundIndex];
    if (score === 0) {
        return { html: '<span style="font-size: 1.5rem; font-weight: bold;">×</span>', classes: ['filled', 'halved'] };
    } else if (score !== undefined) {
        return { html: String(score), classes: ['filled'] };
    }
    return { html: '-', classes: [] };
}

function calculateTotal(player) {
    const total = halveIt.computeTotal(roundScores[player]);
    updateTotal(player, total);
    return total;
}

function notifyChange() {
    if (!onChangeCallback) return;
    const scores = {};
    currentPlayers.forEach(player => { scores[player] = halveIt.computeTotal(roundScores[player]); });
    onChangeCallback({ scores, complete: halveIt.isComplete(currentPlayers, roundScores) });
}

export function hasProgress() {
    return currentPlayers.some(player =>
        rounds.some((_, roundIndex) => roundScores[player][roundIndex] !== undefined)
    );
}

export function getState() {
    return {
        roundScores: JSON.parse(JSON.stringify(roundScores)),
        cpuTurnLive
    };
}

// The full board repaint used both to catch up on a synced setState() and
// (via mount()) to refresh after simulated debug data.
function refreshAll() {
    if (observerMode) {
        renderObserverBoard();
        return;
    }
    currentPlayers.forEach(player => {
        rounds.forEach((_, roundIndex) => updateCell(player, roundIndex, halveItCellContent(player, roundIndex)));
        calculateTotal(player);
    });
}

function applyState(snapshot) {
    roundScores = snapshot.roundScores;
    refreshAll();
    // See x01-game-controller.js's setState for why this matters - a client
    // that only receives a finished game over sync still needs this to
    // reach app.js's handleGameChange and arm the victory modal.
    notifyChange();
}

// Mirrors a synced CPU challenge's live progress onto a non-driving client's
// own board - never re-simulated here, so it always shows exactly what the
// driving client saw. See x01-game-controller.js's identical
// syncObserverCpuOverlay for the full reasoning; this differs only in using
// each throw's own roundContribution (see ai.js) instead of a plain dart
// score, since some rounds here (3 Colors, Score 41) are all-or-nothing.
function syncObserverCpuOverlay(live) {
    if (!live) {
        if (currentCpuOverlay) {
            currentCpuOverlay.el.remove();
            currentCpuOverlay = null;
        }
        observerCpuSeq = 0;
        observerCpuRevealed = 0;
        return;
    }
    if (live.seq !== observerCpuSeq) {
        if (currentCpuOverlay) currentCpuOverlay.el.remove();
        currentCpuOverlay = showCpuThrowOverlay(cpuOverlayTitle(live.player, live.roundIndex));
        observerCpuSeq = live.seq;
        observerCpuRevealed = 0;
    }
    let runningTotal = live.plan.throws.slice(0, observerCpuRevealed).reduce((sum, t) => sum + t.roundContribution, 0);
    while (observerCpuRevealed < live.revealed) {
        const thrown = live.plan.throws[observerCpuRevealed];
        runningTotal += thrown.roundContribution;
        showAimMarker(currentCpuOverlay, thrown, observerCpuRevealed + 1);
        drawCpuDart(currentCpuOverlay, thrown, dartReadout(live.roundIndex, thrown, runningTotal));
        observerCpuRevealed += 1;
    }
    if (live.done) setCpuThrowOutcome(currentCpuOverlay, cpuChallengeOutcomeLabel(live.plan));
}

// Every client except the one actually driving a given CPU challenge mirrors
// it live this way - see x01-game-controller.js's identical setState for the
// full reasoning behind gating on cpuTurnInProgress rather than observerMode.
export function setState(snapshot) {
    if (!cpuTurnInProgress) syncObserverCpuOverlay(snapshot.cpuTurnLive);
    applyState(snapshot);
}

// --- Observer (wall display) board ---
//
// A display-only client gets its own markup (the shared .obs-* wall-display
// styles in styles.css) instead of the interactive rounds grid, and repaints
// the whole thing from roundScores on every synced change - 13 rows by a
// handful of players is cheap, and it keeps the "current challenge"
// highlighting in one place. Halve It has no turn order, so the current
// challenge is derived the same way maybeRunCpuChallenge() derives a CPU's
// turn: the first round somebody hasn't scored yet, and the first player
// (in order) missing a score in it is up next.
let observerBoardEl = null;

function escapeHTML(text) {
    return String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function mountObserverBoard(container) {
    container.innerHTML = '<div class="obs-board obs-halve"></div>';
    observerBoardEl = container.firstElementChild;
    renderObserverBoard();
}

function renderObserverBoard() {
    if (!observerBoardEl) return;
    const cur = rounds.findIndex((_, i) => currentPlayers.some(p => roundScores[p][i] === undefined));
    const upIndex = cur === -1 ? -1 : currentPlayers.findIndex(p => roundScores[p][cur] === undefined);
    const accentOf = i => `--accent:var(--player-${(i % 5) + 1})`;
    const totals = currentPlayers.map(p => halveIt.computeTotal(roundScores[p]));

    let banner;
    if (cur === -1) {
        const best = Math.max(...totals);
        const winners = currentPlayers.filter((_, i) => totals[i] === best);
        banner = `
            <div>
                <div class="obs-banner-label">Game complete</div>
                <div class="obs-banner-big">${winners.map(escapeHTML).join(' &amp; ')} ${winners.length > 1 ? 'tie' : 'wins'}</div>
            </div>`;
    } else {
        banner = `
            <div class="obs-banner-challenge">
                <div class="obs-banner-label">Round ${cur + 1} of ${rounds.length} &middot; current challenge</div>
                <div class="obs-banner-big">${escapeHTML(rounds[cur].description)}</div>
            </div>
            <div class="obs-banner-next">
                <span class="obs-banner-label">Up next</span>
                <span class="obs-banner-name" style="${accentOf(upIndex)}">${escapeHTML(currentPlayers[upIndex])}</span>
            </div>`;
    }

    let table = `<div class="obs-table" style="--obs-n:${currentPlayers.length};--obs-rounds:${rounds.length}">`;
    table += '<div class="obs-th"></div>';
    currentPlayers.forEach((player, i) => {
        table += `<div class="obs-th" style="${accentOf(i)}">${i === upIndex ? '<span class="obs-dot"></span>' : ''}<span class="obs-th-name">${escapeHTML(player)}</span></div>`;
    });
    rounds.forEach((round, r) => {
        const isCur = r === cur;
        table += `<div class="obs-rn${isCur ? ' cur' : ''}">${escapeHTML(round.name)}</div>`;
        currentPlayers.forEach((player, i) => {
            const score = roundScores[player][r];
            let classes = 'obs-cell';
            let text;
            if (score === undefined) {
                if (isCur && i === upIndex) { classes += ' cur up'; text = '&#9654;'; }
                else if (isCur) { classes += ' cur waiting'; text = '&ndash;'; }
                else { classes += ' future'; text = '&middot;'; }
            } else if (score === 0) {
                classes += ' halved' + (isCur ? ' cur' : '');
                text = '&times;';
            } else {
                classes += isCur ? ' cur' : '';
                text = String(score);
            }
            table += `<div class="${classes}">${text}</div>`;
        });
    });
    table += '<div class="obs-tl">Total</div>';
    currentPlayers.forEach((player, i) => {
        table += `<div class="obs-tot" style="${accentOf(i)}">${totals[i]}</div>`;
    });
    table += '</div>';

    observerBoardEl.innerHTML = `<div class="obs-banner">${banner}</div>${table}`;
}

export function mount(container, { players, gameId, onChange, observer = false, cpuPlayers: cpuPlayersOption = {} }) {
    currentPlayers = players;
    currentGameId = gameId;
    onChangeCallback = onChange;
    observerMode = observer;
    cpuPlayers = cpuPlayersOption;
    cpuTurnInProgress = false;
    cpuTurnStopped = false;
    cpuTurnSeq = 0;
    cpuTurnLive = null;
    observerCpuSeq = 0;
    observerCpuRevealed = 0;
    // Defensive, in case a previous mount's CPU challenge is still mid-flight
    // for some reason (app.js is expected to have already called
    // stopCpuTurn() when leaving that earlier game).
    if (currentCpuOverlay) {
        currentCpuOverlay.el.remove();
        currentCpuOverlay = null;
    }
    roundScores = {};
    players.forEach(player => { roundScores[player] = {}; });

    // ?debug=1 simulates a full game (see the block above) so history/stats
    // views can be exercised without clicking through a real one by hand.
    const simulateRandomData = !observer && new URLSearchParams(window.location.search).get('debug');
    if (simulateRandomData) {
        const missRate = Math.random() * 0.2;
        players.forEach(player => {
            rounds.forEach((round, roundIndex) => {
                roundScores[player][roundIndex] = simulateRoundScore(round, missRate);
            });
        });
    }

    if (observer) {
        mountObserverBoard(container);
    } else {
        mountRoundsGrid(container, {
            rounds,
            players,
            cellContent: halveItCellContent,
            onCellClick: openKeypad
        });
    }

    players.forEach(player => calculateTotal(player));

    if (simulateRandomData) {
        const scores = {};
        players.forEach(player => { scores[player] = halveIt.computeTotal(roundScores[player]); });
        saveGameToHistory({ gameId: currentGameId, gameType: halveIt.id, players, scores });
    } else {
        maybeRunCpuChallenge(); // covers a CPU player being first in turn order
    }
}

// Called from outside (app.js, when leaving the game screen) to stop a CPU's
// challenge in its tracks rather than letting it keep animating/committing
// in the background - see x01-game-controller.js's identical stopCpuTurn.
export function stopCpuTurn() {
    cpuTurnStopped = true;
    cpuTurnLive = null;
    if (currentCpuOverlay) {
        currentCpuOverlay.el.remove();
        currentCpuOverlay = null;
    }
}

// --- Computer-controlled challenges ---
//
// Halve It has no built-in turn order (any cell, any player, any round, any
// time) - so unlike x01's activeIndex, "is it a CPU's turn" has to be
// derived fresh from the grid itself: flatten (round, player) into one
// round-robin sequence - round 0 for every player in currentPlayers order,
// then round 1 for every player, and so on - and a CPU throws for its own
// current next unplayed round the moment it's next up in that sequence.
// For anyone but the first player in a round, "next up" just means everyone
// listed before them already has a score in that same round. The first
// player in a round has nobody before them *in that round* - their previous
// turn in the sequence is actually the *last* player's turn in the
// *previous* round, so that's what has to be checked instead (an earlier
// version skipped this, which let a CPU listed first in currentPlayers race
// through every one of its own rounds uncontested, since "everyone before
// it" was vacuously empty every time). Only one CPU challenge ever runs at a
// time (cpuTurnInProgress); committing one calls this again (see
// commitCpuChallenge) so a chain of several CPU players in a row keeps going
// on its own, the same cascade x01-game-controller.js's own maybeRunCpuTurn
// relies on.
function isPlayerUpNext(playerIndex, roundIndex) {
    if (playerIndex > 0) {
        return currentPlayers
            .slice(0, playerIndex)
            .every(p => roundScores[p][roundIndex] !== undefined);
    }
    if (roundIndex === 0) return true; // the very first turn of the whole match
    const lastPlayer = currentPlayers[currentPlayers.length - 1];
    return roundScores[lastPlayer][roundIndex - 1] !== undefined;
}

function maybeRunCpuChallenge() {
    if (observerMode || cpuTurnInProgress || cpuTurnStopped) return;
    for (let playerIndex = 0; playerIndex < currentPlayers.length; playerIndex++) {
        const player = currentPlayers[playerIndex];
        const throwProfile = cpuPlayers[player];
        if (!throwProfile) continue;
        const roundIndex = rounds.findIndex((_, i) => roundScores[player][i] === undefined);
        if (roundIndex === -1) continue; // this CPU has already filled every round
        if (!isPlayerUpNext(playerIndex, roundIndex)) continue;
        runCpuChallenge(player, roundIndex);
        return; // one at a time - its own commit re-triggers this for whoever's next
    }
}

function cpuOverlayTitle(player, roundIndex) {
    return `${player} — ${rounds[roundIndex].name}`;
}

// What the overlay's readout shows after each dart lands - the running
// total for every round except 3 Colors, where the actual color a dart
// landed as (not a mostly-0 running total - see ai.js's own roundContribution
// comment on why every dart but the last scores 0 there) is what actually
// tells you something useful while the round's still in progress, e.g.
// "T20 — Red". A genuine miss (no wedge, no bull) shows as "Miss" rather
// than a color that doesn't exist.
function dartReadout(roundIndex, thrown, runningTotal) {
    if (rounds[roundIndex].name !== '3 Colors') return runningTotal;
    const color = wedgeColor(thrown.result.number, thrown.result.ring);
    return color ? color.charAt(0).toUpperCase() + color.slice(1) : 'Miss';
}

// The overlay's final readout line for a finished (or just-mirrored)
// challenge - shared by the driving client's own animateCpuChallenge and an
// observer's syncObserverCpuOverlay, same reasoning as
// x01-game-controller.js's own cpuTurnOutcomeLabel.
function cpuChallengeOutcomeLabel(plan) {
    if (plan.outcome === 'bust') return 'BUST';
    if (plan.outcome === 'halved') return 'Halved!';
    return String(plan.totalScore);
}

// Reveals an already-simulated challenge's darts one at a time on the
// read-only full-board overlay - purely presentational, same as
// x01-game-controller.js's animateCpuTurn, which this otherwise mirrors
// closely (including broadcasting cpuTurnLive at every step so other
// clients can mirror the reveal live - see syncObserverCpuOverlay). Uses
// each throw's roundContribution (see ai.js) for the running total shown,
// not the raw dart score, since some rounds don't count every dart at face
// value (or, for 3 Colors/Score 41, don't count anything until the third) -
// dartReadout then decides what that actually displays as (that running
// total for most rounds, or 3 Colors' own color-per-dart readout instead).
async function animateCpuChallenge(player, roundIndex, plan) {
    cpuTurnSeq += 1;
    const turnSeq = cpuTurnSeq;
    const overlay = showCpuThrowOverlay(cpuOverlayTitle(player, roundIndex));
    currentCpuOverlay = overlay;
    cpuTurnLive = { seq: turnSeq, player, roundIndex, plan, revealed: 0, done: false };
    notifyChange();
    await waitBeforeFirstDart();
    let runningTotal = 0;
    for (let i = 0; i < plan.throws.length; i++) {
        if (cpuTurnStopped) return;
        const thrown = plan.throws[i];
        runningTotal += thrown.roundContribution;
        await revealCpuDart(overlay, thrown, dartReadout(roundIndex, thrown, runningTotal), i + 1);
        cpuTurnLive = { seq: turnSeq, player, roundIndex, plan, revealed: cpuTurnLive.revealed + 1, done: false };
        notifyChange();
    }
    if (cpuTurnStopped) return;
    cpuTurnLive = { seq: turnSeq, player, roundIndex, plan, revealed: plan.throws.length, done: true };
    notifyChange();
    await finishCpuThrowOverlay(overlay, cpuChallengeOutcomeLabel(plan));
    currentCpuOverlay = null;
    cpuTurnLive = null;
}

function commitCpuChallenge(player, roundIndex, plan) {
    roundScores[player][roundIndex] = plan.totalScore;
    updateCell(player, roundIndex, halveItCellContent(player, roundIndex));
    calculateTotal(player);
    notifyChange();
    maybeRunCpuChallenge();
}

// Runs one CPU player's challenge for one round end to end: simulate ->
// animate -> commit. A brief pause (waitBetweenCpuTurns, shared with x01)
// precedes the overlay whenever the match already has some progress, so a
// challenge that immediately follows another cell being filled - CPU or
// human - never covers the grid again before that cell's own update was
// actually visible; skipped only for the very first score of a fresh match,
// where there's no prior cell to let register.
async function runCpuChallenge(player, roundIndex) {
    cpuTurnInProgress = true;
    if (hasProgress()) await waitBetweenCpuTurns();
    if (cpuTurnStopped) { cpuTurnInProgress = false; return; }

    const round = rounds[roundIndex];
    const throwProfile = cpuPlayers[player];
    const plan = simulateAiChallenge({ round, throwProfile });
    await animateCpuChallenge(player, roundIndex, plan);
    cpuTurnInProgress = false;
    // Never commits a challenge that stopCpuTurn() interrupted mid-reveal.
    if (cpuTurnStopped) return;
    commitCpuChallenge(player, roundIndex, plan);
}

function openKeypad(player, roundIndex) {
    currentCell = { player, roundIndex };
    const round = rounds[roundIndex];
    const currentScore = roundScores[player][roundIndex];

    document.getElementById('keypadTitle').textContent = `${player} - ${round.name}`;
    // Show existing score or empty string if no score yet
    document.getElementById('keypadDisplay').textContent = currentScore !== undefined ? currentScore : '';
    showAnimatedModal('keypadModal', 'keypadBackdrop');

    // Reset entry state (first-input flag, dart tally) for the new widget
    resetKeypadEntryState();

    // Ask the game definition which input widget this round needs
    const widget = halveIt.inputWidgetFor(round);
    dartMode = widget.type === 'dartboard-number' || widget.type === 'dartboard-bull';
    dartTarget = dartMode ? widget.target : 0;
    dartMaxMult = dartMode ? widget.maxMultiplier : 3;
    document.getElementById('keypadDisplay').classList.toggle('dart-input', dartMode);

    const keypadHTML = {
        'dartboard-bull': () => getBullKeypadHTML(),
        'fortyone': () => getFortyOneKeypadHTML(),
        'dartboard-number': () => getDartKeypadHTML(),
        'numeric': () => getNumericKeypadHTML()
    };
    document.getElementById('keypadGrid').innerHTML = keypadHTML[widget.type]();

    if (widget.type === 'dartboard-number') {
        renderDartboard(widget.target);
    } else if (widget.type === 'dartboard-bull') {
        renderBullDartboard();
    }
}

function closeKeypad() {
    hideAnimatedModal('keypadModal', 'keypadBackdrop');
    currentCell = null;
}

// Clones the cached dartboard, zooms into the target number's wedge, and
// wires its single/double/triple areas to feed dart taps into the keypad.
function renderDartboard(target) {
    const mount = document.getElementById('dartboardMount');
    mountDartboardForTarget(mount, target, {
        onTap: (mult) => dartTap(mult),
        isStillRelevant: () => dartMode && dartTarget === target
    });
}

// Clones the cached dartboard, zooms into the bull, and wires the outer
// bull/bullseye to feed dart taps into the keypad.
function renderBullDartboard() {
    const mount = document.getElementById('dartboardMount');
    mountDartboardForBull(mount, {
        onTap: (mult) => dartTap(mult),
        isStillRelevant: () => dartMode && dartTarget === 25
    });
}

function fortyOneHit() {
    kpFortyOneHit(document.getElementById('keypadDisplay'));
}

function fortyOneMiss() {
    kpFortyOneMiss(document.getElementById('keypadDisplay'));
}

function keypadInput(num) {
    digitInput(document.getElementById('keypadDisplay'), num);
}

function keypadClear() {
    if (dartMode) {
        dartClear();
        return;
    }
    digitClear(document.getElementById('keypadDisplay'));
}

function keypadBackspace() {
    if (dartMode) {
        dartUndo();
        return;
    }
    digitBackspace(document.getElementById('keypadDisplay'));
}

function dartTap(mult) {
    if (mult > dartMaxMult) return;
    kpDartTap(document.getElementById('keypadDisplay'), mult, dartTarget);
}

function dartHalve() {
    kpDartHalve(document.getElementById('keypadDisplay'));
}

function dartUndo() {
    kpDartUndo(document.getElementById('keypadDisplay'), dartTarget);
    removeLastDartMark(document.getElementById('dartboardMount'));
}

function dartClear() {
    kpDartClear(document.getElementById('keypadDisplay'));
    clearDartTaps(document.getElementById('dartboardMount'));
}

function keypadSubmit() {
    if (!currentCell) return;

    const displayValue = document.getElementById('keypadDisplay').textContent;
    let { player, roundIndex } = currentCell;

    // If display is empty, don't submit anything
    if (displayValue === '') {
        // Remove the score for this round
        delete roundScores[player][roundIndex];
        updateCell(player, roundIndex, halveItCellContent(player, roundIndex));

        calculateTotal(player);
        notifyChange();
        closeKeypad();
        maybeRunCpuChallenge(); // clearing a round could newly satisfy a CPU waiting on it
        return;
    }

    const score = parseInt(displayValue);

    // Store the score
    roundScores[player][roundIndex] = score;
    updateCell(player, roundIndex, halveItCellContent(player, roundIndex));

    calculateTotal(player);
    notifyChange();

    closeKeypad();
    maybeRunCpuChallenge();

    // Auto-advance to the next player/round's keypad, unless the game just
    // finished (nothing left to advance to).
    if (!halveIt.isComplete(currentPlayers, roundScores) && autoOpenKeypadForNextRound) {
        let currentPlayerIndex = currentPlayers.indexOf(player) + 1;
        if (currentPlayerIndex > currentPlayers.length - 1) {
            currentPlayerIndex = 0;
            roundIndex++;
        }
        setTimeout(function() {
            openKeypad(currentPlayers[currentPlayerIndex], roundIndex);
        }, 500);
    }
}

// Keypad-specific keyboard shortcuts - safe to always register, since these
// only ever act while this game's own keypad modal happens to be open.
document.addEventListener('keydown', function(e) {
    if (!isModalOpen('keypadModal')) return;
    if (dartMode && e.key >= '1' && e.key <= String(dartMaxMult)) {
        dartTap(Number(e.key));
    } else if (dartMode && e.key === '0') {
        dartHalve();
    } else if (!dartMode && e.key >= '0' && e.key <= '9') {
        keypadInput(e.key);
    } else if (e.key === 'Enter') {
        keypadSubmit();
    } else if (e.key === 'Escape') {
        closeKeypad();
    } else if (e.key === 'Backspace') {
        e.preventDefault();
        keypadBackspace();
    }
});

// Referenced from onclick="..." attributes in index.html's static keypad
// modal markup and in components/keypad.js's generated button HTML - those
// always resolve against global scope.
Object.assign(window, {
    closeKeypad, keypadInput, keypadClear, keypadBackspace, keypadSubmit,
    dartClear, dartHalve, dartUndo, fortyOneHit, fortyOneMiss
});

export default { definition: halveIt, mount, hasProgress, getState, setState, stopCpuTurn };
